import { styled } from "@monolith-forensics/monolith-ui";
import { LinkIcon, LockIcon } from "lucide-react";
import { BadgedIcon } from "@/Features/_components/BadgedIcon";
import { EVIDENCE_FILES_HELP_URL } from "./evidence-files.constants";
import { useEvidenceItemSummary } from "./evidence-files.query";
import { useCaseFiles } from "@/Features/Files/Hooks/useCaseFiles";
import { isEvidencePhoto } from "@/Features/EvidencePhotos/evidence-photos.utils";
import {
  evidenceFileNoun,
  evidenceItemLabel,
  isManagedFolder,
} from "./evidence-files.utils";

const StyledTooltip = styled.div`
  max-width: 260px;
  white-space: normal;

  a {
    margin-left: 4px;
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

export const LearnMoreLink: React.FC<{ label?: string }> = ({
  label = "Learn more",
}) => (
  <a
    href={EVIDENCE_FILES_HELP_URL}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`${label} about evidence files`}
    onClick={(e) => e.stopPropagation()}
  >
    {label}
  </a>
);

/** A tooltip body: the message, then Learn more. */
export const ManagedTooltip: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <StyledTooltip>
    {children}
    <LearnMoreLink />
  </StyledTooltip>
);

// Mounted only while the tooltip is open, so the item lookup waits for a
// hover instead of running for every row in the table.
const EvidenceFileTooltip: React.FC<{
  evidenceId?: number | null;
  name: string;
}> = ({ evidenceId, name }) => {
  const { surface } = useCaseFiles();
  const onItemTab = surface === "evidence-files-tab";
  const { data } = useEvidenceItemSummary(evidenceId, { enabled: !onItemTab });

  if (onItemTab) {
    // Already on the item: name the other places, like the modal alerts.
    return (
      <ManagedTooltip>
        This {evidenceFileNoun({ name })} is also in{" "}
        {isEvidencePhoto({ name })
          ? "Case Files and this item's Photos tab"
          : "Case Files"}
        . Changes here show there too.
      </ManagedTooltip>
    );
  }

  return (
    <ManagedTooltip>
      This file is linked to {evidenceItemLabel(data?.evidence_number)}. Changes
      here also show in its Files tab.
    </ManagedTooltip>
  );
};

const ICON_STYLE = { height: "1.5rem" };

/**
 * The name cell icon for a managed row (ENG-2133): managed folders get a lock
 * badge, evidence files keep their file type icon and get a link badge.
 */
export const ManagedRowIcon: React.FC<{
  row: {
    name: string;
    source_type?: string | null;
    source_id?: number | null;
  };
  src: string;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}> = ({ row, src, onError }) => {
  if (isManagedFolder(row)) {
    return (
      <BadgedIcon
        icon={<img src={src} alt="" style={ICON_STYLE} draggable={false} />}
        badge={<LockIcon size={8} strokeWidth={3} />}
        label="System managed folder"
        data-testid="managed-folder-icon"
        tooltip={
          <ManagedTooltip>
            This is a system managed folder and can't be renamed, deleted, or
            moved.
          </ManagedTooltip>
        }
      />
    );
  }

  return (
    <BadgedIcon
      icon={
        <img
          src={src}
          onError={onError}
          alt=""
          style={ICON_STYLE}
          draggable={false}
        />
      }
      badge={<LinkIcon size={8} strokeWidth={3} />}
      label="File linked to an evidence item"
      data-testid="evidence-file-icon"
      tooltip={
        <EvidenceFileTooltip evidenceId={row.source_id} name={row.name} />
      }
    />
  );
};
